// src\services\UserAuthService.js

// Import necessary dependencies
import { Auth } from 'aws-amplify';

/**
 * Fetches the secret hash required for Cognito operations.
 * @param {string} username - The username for which to fetch the secret hash.
 * @returns {Promise<string>} - The secret hash or an error message.
 */
async function getSecretHash(username) {
  try {
    const response = await fetch('/getSecretHash', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch secret hash from server.');
    }

    const data = await response.json();
    return data.secretHash;
  } catch (err) {
    console.error('Error fetching secret hash:', err);
    throw new Error('Failed to fetch secret hash.');
  }
}

/**
 * Registers a new user with AWS Cognito using their username and password.
 * @param {string} username - The username of the user to register.
 * @param {string} password - The password of the user to register.
 * @returns {Promise<Object>} - The Cognito user object or an error message.
 */
async function registerUser(username, password) {
  const secretHash = await getSecretHash(username);

  try {
    const user = await Auth.signUp({
      username,
      password,
      secretHash,
      // ... any other required attributes
    });
    return user;
  } catch (err) {
    console.error('Error signing up:', err);
    throw new Error('Failed to register user.');
  }
}

/**
 * Signs out the currently authenticated user.
 * @returns {Promise<Object>} - A promise that resolves when the sign-out process is complete.
 */
async function signOut() {
  try {
    await Auth.signOut();
    console.log('Successfully signed out');
  } catch (err) {
    console.error('Error signing out:', err);
    throw new Error('Failed to sign out.');
  }
}

// You can define other functions related to user authentication here...

export default {
  registerUser,
  signOut,
  // ... export other functions or objects as needed
};
